import { ChevronLeft, ChevronRight } from "lucide-react";

interface TourNavProps {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onJump: (i: number) => void;
}

const TourNav = ({ index, total, onPrev, onNext, onJump }: TourNavProps) => {
  const atStart = index === 0;
  const atEnd = index === total - 1;

  return (
    <div className="absolute bottom-0 left-0 right-0 z-20 pb-4 sm:pb-6">
      {/* Progress bar */}
      <div className="px-6 sm:px-10 mb-3">
        <div className="h-1 rounded-full bg-white/70 backdrop-blur overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${((index + 1) / total) * 100}%`,
              background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
            }}
          />
        </div>
      </div>

      {/* Controls — Back + Next grouped on the left, dots fill the rest */}
      <div className="flex items-center gap-3 sm:gap-4 px-4 sm:px-10">
        <div className="flex items-center gap-2">
          <button
            onClick={onPrev}
            disabled={atStart}
            aria-label="Previous slide"
            className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur border border-[#E5EAF0] px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#222B45] hover:shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <button
            onClick={onNext}
            disabled={atEnd}
            aria-label="Next slide"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
          >
            <span>{atEnd ? "Done" : "Next"}</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Compact dot bar — fills the remaining space */}
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div className="flex items-center gap-1.5 max-w-full overflow-hidden">
            {Array.from({ length: total }).map((_, i) => {
              const active = i === index;
              return (
                <button
                  key={i}
                  onClick={() => onJump(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={active ? "step" : undefined}
                  className="group flex-shrink-0 p-1"
                  title={`Page ${i + 1}`}
                >
                  <span
                    className="block rounded-full transition-all duration-300"
                    style={{
                      width: active ? 22 : 6,
                      height: 6,
                      background: active
                        ? "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)"
                        : "#CBD5E1",
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourNav;
